import { useEffect, useMemo, useState } from "react";
import { api, fmtDate, fmtDateTime, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Phone, Mail, Users as UsersIcon, MessageSquare, Activity as ActivityIcon, Plus, Edit2, Trash2, X } from "lucide-react";
import { Pill } from "@/components/badges";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const TYPE_META = {
  Call:    { icon: Phone,          color: "bg-emerald-50 text-emerald-700 border border-emerald-200" },
  Email:   { icon: Mail,           color: "bg-sky-50 text-sky-700 border border-sky-200" },
  Meeting: { icon: UsersIcon,      color: "bg-violet-50 text-violet-700 border border-violet-200" },
  SMS:     { icon: MessageSquare,  color: "bg-amber-50 text-amber-700 border border-amber-200" },
  Other:   { icon: ActivityIcon,   color: "bg-slate-100 text-slate-700 border border-slate-200" },
};
const TYPES = ["Call", "Email", "Meeting", "SMS", "Other"];

function todayYmd() {
  const d = new Date();
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 10);
}

export default function ActivityTab({ entityType, entityId, ownerCanEdit = true }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/activities?entity_type=${entityType}&entity_id=${entityId}`);
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [entityType, entityId]);

  const grouped = useMemo(() => {
    // group by activity_date
    const groups = {};
    for (const a of items) {
      const k = a.activity_date || "—";
      (groups[k] = groups[k] || []).push(a);
    }
    return Object.entries(groups).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [items]);

  const canEdit = (a) => user && (user.role === "admin" || a.created_by === user.id) && ownerCanEdit;

  const onDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/activities/${toDelete.id}`);
      toast.success("Activity removed");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="activity-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">Activity Log</h3>
          <p className="text-xs text-slate-500">Calls, emails, meetings and other touchpoints logged on this {entityType}.</p>
        </div>
        <Button
          data-testid="new-activity-btn"
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="bg-[#1B4F8B] hover:bg-[#163e6e]"
        >
          <Plus className="w-4 h-4 mr-1" /> Log activity
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="eyb-card p-10 text-center" data-testid="activity-empty">
          <ActivityIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No activities logged yet.</p>
          <p className="text-xs text-slate-400 mt-1">Track your calls, emails and meetings to keep a complete history.</p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="activity-list">
          {grouped.map(([date, list]) => (
            <div key={date}>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">{fmtDate(date)}</div>
              <div className="space-y-2">
                {list.map((a) => {
                  const meta = TYPE_META[a.type] || TYPE_META.Other;
                  const Icon = meta.icon;
                  return (
                    <div key={a.id} className="eyb-card p-4" data-testid={`activity-${a.id}`}>
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 shrink-0 rounded-lg bg-slate-50 flex items-center justify-center">
                          <Icon className="w-4 h-4 text-slate-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Pill className={meta.color}>{a.type}</Pill>
                            {a.subject && <span className="text-sm font-semibold text-slate-900">{a.subject}</span>}
                          </div>
                          <p className="text-sm text-slate-700 mt-1.5 whitespace-pre-line">{a.notes}</p>
                          <div className="text-[11px] text-slate-400 mt-2">
                            Logged by <span className="font-semibold text-slate-600">{a.created_by_name || "—"}</span> · {fmtDateTime(a.created_at)}
                            {a.updated_at && a.updated_at !== a.created_at ? <> · edited {fmtDateTime(a.updated_at)}</> : null}
                          </div>
                        </div>
                        {canEdit(a) && (
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              data-testid={`activity-edit-${a.id}`}
                              onClick={() => { setEditing(a); setShowForm(true); }}
                              className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-900"
                              title="Edit"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              data-testid={`activity-delete-${a.id}`}
                              onClick={() => setToDelete(a)}
                              className="p-1.5 rounded-md hover:bg-rose-50 text-slate-500 hover:text-rose-600"
                              title="Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <ActivityFormModal
          entityType={entityType}
          entityId={entityId}
          initial={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="activity-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this activity?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete ? <>This will permanently remove the <span className="font-semibold">{toDelete.type}</span> entry from {fmtDate(toDelete.activity_date)}.</> : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="activity-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} data-testid="activity-delete-action" className="bg-rose-600 hover:bg-rose-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ActivityFormModal({ entityType, entityId, initial, onClose, onSaved }) {
  const editing = !!initial;
  const [form, setForm] = useState(() => ({
    activity_date: initial?.activity_date || todayYmd(),
    type: initial?.type || "Call",
    subject: initial?.subject || "",
    notes: initial?.notes || "",
  }));
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.notes.trim()) { toast.error("Notes are required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/activities/${initial.id}`, {
          activity_date: form.activity_date,
          type: form.type,
          subject: form.subject || null,
          notes: form.notes,
        });
        toast.success("Activity updated");
      } else {
        await api.post("/activities", {
          entity_type: entityType,
          entity_id: entityId,
          activity_date: form.activity_date,
          type: form.type,
          subject: form.subject || null,
          notes: form.notes,
        });
        toast.success("Activity logged");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-[520px]" data-testid="activity-form-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#29ABE2]/10 text-[#187199] flex items-center justify-center">
              <ActivityIcon className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle>{editing ? "Edit activity" : "Log new activity"}</DialogTitle>
              <DialogDescription>Track customer touchpoints — calls, emails, meetings and more.</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="act-date">Date</Label>
              <Input
                id="act-date"
                data-testid="activity-date-input"
                type="date"
                value={form.activity_date}
                onChange={(e) => setForm({ ...form, activity_date: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="act-type">Type</Label>
              <select
                id="act-type"
                data-testid="activity-type-select"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
                required
              >
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="act-subject">Subject <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Input
              id="act-subject"
              data-testid="activity-subject-input"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder='e.g. "Intro call — pricing options"'
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="act-notes">Notes</Label>
            <textarea
              id="act-notes"
              data-testid="activity-notes-input"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40"
              placeholder="What was discussed, next steps, follow-ups…"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving} data-testid="activity-form-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="bg-[#1B4F8B] hover:bg-[#163e6e]" data-testid="activity-form-submit">
              {saving ? "Saving…" : editing ? "Save changes" : "Log activity"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
